import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonSelect from '../../Common/CommonSelect';
import CompanyForm from '../Companies/CompanyForm';
import { useSecondarySidebar } from '../../../contexts/SecondarySidebarContext';
import { fetchSettings } from '../../../store/modules/supplier/supplierActions';
import useFormSettings from '../../../hooks/useFormSettings';
import CommonInput from '../../Common/CommonInput';
import { useHasPermission } from '../../../utils/permissionUtils';

const SupplierForm = ({ initialData, onSave, isLoading, onClose }) => {
    const dispatch = useDispatch();
    const hasCreatePermission = useHasPermission(['create suppliers']);
    const hasUpdatePermission = useHasPermission(['update suppliers']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('supplier', fetchSettings);
    const { vendor_types, vat_groups, countries, currencies } = settings || {};
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        name: {},
        email: '',
        phone_number: '',
        address_name: {},
        address: {},
        building: '',
        flat: '',
        block: '',
        area: '',
        mobile_number: '',
        fax: '',
        website: '',
        remark: {},
        country: null,
        currency: null,
        vendor_type: null,
        vat_group: null,
        is_domestic: false,
        is_active: true
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);


    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({
                ...prev,
                ...initialData,
                name: initialData.name || {},
                address_name: initialData.address_name || {},
                address: initialData.address || {},
                email: initialData.email || '',
                phone_number: initialData.phone_number || '',
                building: initialData.building || '',
                flat: initialData.flat || '',
                block: initialData.block || '',
                area: initialData.area || '',
                mobile_number: initialData.mobile_number || '',
                fax: initialData.fax || '',
                website: initialData.website || '',
                remark: initialData.remark || {},
                country: initialData.country || null,
                currency: initialData.currency || null,
                vendor_type: initialData.vendor_type || null,
                vat_group: initialData.vat_group || null,
                is_domestic: initialData.is_domestic ?? false,
                is_active: initialData.is_active ?? true
            }));
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleVendorTypeChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            vendor_type: selectedOption
        }));

        if (errors.vendor_type) {
            setErrors(prev => ({...prev, vendor_type: ''}));
        }
    };

    const handleVatGroupChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            vat_group: selectedOption
        }));

        if (errors.vat_group) {
            setErrors(prev => ({...prev, vat_group: ''}));
        }
    };

    const handleCountryChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            country: selectedOption
        }));

        if (errors.country) {
            setErrors(prev => ({...prev, country: ''}));
        }
    };

    const handleCurrencyChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            currency: selectedOption
        }));

        if (errors.currency) {
            setErrors(prev => ({...prev, currency: ''}));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    const { toggleSecondarySidebar, closeSecondarySidebar } = useSecondarySidebar();
    const handleCompanyCreate = useCallback(() => {
        toggleSecondarySidebar({
          className: `right-sidebar-md`,
          title: `Create Company`,
          content: (
            <CompanyForm
                onSave={async (data) => {
                    try {
                        
                    } catch (error) {
 
                    }
                }}
                onClose={closeSecondarySidebar}
            />
          ),
        });
    }, [toggleSecondarySidebar, closeSecondarySidebar]);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableInput
                            label="Supplier Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <div className="mb-3">
                            <CommonInput
                                name="email"
                                label="Email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Leave empty or a valid email"
                                disabled={isSubmitting}
                                errors={errors}
                                required={false}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-3">
                            <label className="form-label">
                                Phone Number
                                <span className="text-danger ms-1">*</span>
                            </label>
                            <input
                                type="tel"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                className={`form-control ${errors.phone_number ? 'is-invalid' : ''}`}
                                placeholder="Enter phone number"
                                disabled={isSubmitting}
                            />
                            {errors.phone_number && (
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.phone_number) ? errors.phone_number[0] : errors.phone_number}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="vendor_type"
                            label="Vendor Type"
                            value={formData.vendor_type}
                            onChange={handleVendorTypeChange}
                            options={vendor_types}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select vendor type..."
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Address Name"
                            name="address_name"
                            rows={1}
                            value={formData.address_name}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Address"
                            name="address"
                            rows={1}
                            value={formData.address}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-3">
                            <label className="form-label">
                                Building
                            </label>
                            <input
                                type="text"
                                name="building"
                                value={formData.building}
                                onChange={handleChange}
                                className={`form-control ${errors.building ? 'is-invalid' : ''}`}
                                placeholder="Enter phone number"
                                disabled={isSubmitting}
                            />
                            {errors.building && (
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.building) ? errors.building[0] : errors.building}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-3">
                            <label className="form-label">
                                Flat
                            </label>
                            <input
                                type="text"
                                name="flat"
                                value={formData.flat}
                                onChange={handleChange}
                                className={`form-control ${errors.flat ? 'is-invalid' : ''}`}
                                placeholder="Enter flat"
                                disabled={isSubmitting}
                            />
                            {errors.flat && (
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.flat) ? errors.flat[0] : errors.flat}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-3">
                            <label className="form-label">
                                Block
                            </label>
                            <input
                                type="text"
                                name="block"
                                value={formData.block}
                                onChange={handleChange}
                                className={`form-control ${errors.block ? 'is-invalid' : ''}`}
                                placeholder="Enter block"
                                disabled={isSubmitting}
                            />
                            {errors.block && (
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.block) ? errors.block[0] : errors.block}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-3">
                            <label className="form-label">
                                Area
                            </label>
                            <input
                                type="text"
                                name="area"
                                value={formData.area}
                                onChange={handleChange}
                                className={`form-control ${errors.area ? 'is-invalid' : ''}`}
                                placeholder="Enter area"
                                disabled={isSubmitting}
                            />
                            {errors.area && (
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.area) ? errors.area[0] : errors.area}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="country"
                            label="Country"
                            value={formData.country}
                            onChange={handleCountryChange}
                            options={countries}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select country..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="currency"
                            label="Currency"
                            value={formData.currency}
                            onChange={handleCurrencyChange}
                            options={currencies}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select currency..."
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-3">
                        <div className="mb-3">
                            <label className="form-label">
                                Mobile Number
                            </label>
                            <input
                                type="text"
                                name="mobile_number"
                                value={formData.mobile_number}
                                onChange={handleChange}
                                className={`form-control ${errors.mobile_number ? 'is-invalid' : ''}`}
                                placeholder="Enter mobile number"
                                disabled={isSubmitting}
                            />
                            {errors.mobile_number && (
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.mobile_number) ? errors.mobile_number[0] : errors.mobile_number}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="mb-3">
                            <label className="form-label">
                                Fax
                            </label>
                            <input
                                type="text"
                                name="fax"
                                value={formData.fax}
                                onChange={handleChange}
                                className={`form-control ${errors.fax ? 'is-invalid' : ''}`}
                                placeholder="Enter fax"
                                disabled={isSubmitting}
                            />
                            {errors.fax && (
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.fax) ? errors.fax[0] : errors.fax}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="mb-3">
                            <label className="form-label">
                                Website
                            </label>
                            <input
                                type="text"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                className={`form-control ${errors.website ? 'is-invalid' : ''}`}
                                placeholder="Enter website"
                                disabled={isSubmitting}
                            />
                            {errors.website && (
                                <div className="invalid-feedback">
                                    {Array.isArray(errors.website) ? errors.website[0] : errors.website}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <TranslatableTextarea
                            label="Remark"
                            name="remark"
                            rows={1}
                            value={formData.remark}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="vat_group"
                            label="VAT Group"
                            value={formData.vat_group}
                            onChange={handleVatGroupChange}
                            options={vat_groups}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select VAT group..."
                        />
                    </div>
                    <div className="col-md-4">
                        <div className="my-3">
                            <label className="form-check-label m-0 me-2 user-select-none" htmlFor="is_domestic">Is Supplier Domestic ?</label>
                            <div className="form-check form-switch form-switch-md m-0">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    id="is_domestic" 
                                    name="is_domestic"
                                    checked={formData.is_domestic}
                                    onChange={(e) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            is_domestic: e.target.checked
                                        }));
                                    }}
                                    disabled={isSubmitting}
                                /> 
                                {formData.is_domestic ? 'Yes' : 'No'}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mb-3">
                    <div className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleChange}
                            id="is_active"
                            disabled={isSubmitting}
                        />
                        <label className="form-check-label user-select-none" htmlFor="is_active">
                            Active
                        </label>
                    </div>
                </div>
            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default SupplierForm; 